import { motion } from "framer-motion";
import { BrainCircuit, Activity, Timer, HeartPulse, Users, Dumbbell, Repeat, ChefHat, Watch, Video } from "lucide-react";
import { FadeUp, Eyebrow } from "./Reveal";
import { RecipeCarousel } from "./RecipeCarousel";

export const Features = () => (
  <section id="features" data-testid="features-section" className="relative py-32 scroll-mt-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <FadeUp>
        <Eyebrow>The System</Eyebrow>
        <h2 className="font-display font-extrabold uppercase tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl mt-4 max-w-3xl">
          Every feature earns <span className="text-volt">its minute.</span>
        </h2>
      </FadeUp>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-4">
        <FadeUp className="md:col-span-8 md:row-span-2" delay={0.05}>
          <div className="group relative h-full min-h-[420px] border border-white/10 bg-carbon p-8 sm:p-10 overflow-hidden hover:border-volt/40 transition-colors duration-300">
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "radial-gradient(circle at 50% 0%, rgba(255,107,0,0.08) 0%, transparent 70%)" }} />
            <BrainCircuit className="h-8 w-8 text-volt" />
            <h3 className="font-display font-extrabold uppercase text-2xl sm:text-3xl mt-6 leading-tight">
              Adaptive Session Engine
            </h3>
            <p className="mt-4 max-w-md text-base sm:text-lg text-neutral-400 leading-relaxed">
              Tell Fit50 how many minutes you have and it builds the session around them —
              warm-up, work blocks, rest and cooldown compressed or expanded automatically.
              The workout fits your calendar, never the other way around.
            </p>
            <div className="absolute bottom-0 left-0 right-0 px-8 sm:px-10 pb-8">
              <div className="flex items-end gap-2 h-28">
                {[20, 10, 30, 20, 50, 10, 40, 30, 20, 50].map((h, i) => (
                  <motion.div
                    key={i}
                    initial={{ height: 0 }}
                    whileInView={{ height: `${h * 2}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.3 + i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                    className={`flex-1 ${i >= 8 ? "bg-volt" : "bg-white/15"} group-hover:bg-volt/80 transition-colors duration-300`}
                  />
                ))}
              </div>
              <p className="font-mono text-[10px] tracking-widest uppercase text-neutral-500 mt-3">
                Session Lengths Auto-Fit To A Real Week
              </p>
            </div>
          </div>
        </FadeUp>

        <FadeUp className="md:col-span-4" delay={0.1}>
          <div className="h-full border border-white/10 bg-carbon p-8 hover:border-volt/40 hover:bg-steel transition-colors duration-300">
            <Activity className="h-7 w-7 text-volt" />
            <p className="font-display font-black text-6xl mt-6 text-volt">10&prime;</p>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-neutral-400 mt-2">Minimum Effective Dose</p>
            <p className="mt-4 text-sm text-neutral-500 leading-relaxed">
              Our floor, not a shortcut. Ten programmed minutes hit 80% of the benefit — and make "no time" physically impossible.
            </p>
          </div>
        </FadeUp>

        <FadeUp className="md:col-span-4" delay={0.15}>
          <div className="h-full border border-white/10 bg-carbon p-8 hover:border-volt/40 hover:bg-steel transition-colors duration-300">
            <Timer className="h-7 w-7 text-volt" />
            <h3 className="font-display font-bold uppercase text-lg sm:text-xl tracking-wide mt-6">
              Auto-Compressed Rest Timers
            </h3>
            <p className="mt-3 text-sm text-neutral-500 leading-relaxed">
              Rest windows shrink or stretch to match your chosen duration — intensity stays high, the clock stays honest.
            </p>
          </div>
        </FadeUp>

        <FadeUp className="md:col-span-6" delay={0.2}>
          <div className="h-full border border-white/10 bg-carbon p-8 hover:border-volt/40 hover:bg-steel transition-colors duration-300">
            <HeartPulse className="h-7 w-7 text-volt" />
            <h3 className="font-display font-bold uppercase text-lg sm:text-xl tracking-wide mt-6">
              Live Heart-Rate Zones
            </h3>
            <p className="mt-3 text-sm text-neutral-500 leading-relaxed">
              Pair your wearable and Fit50 steers every interval by live heart rate — pushing or pulling back so your minutes are never wasted.
            </p>
          </div>
        </FadeUp>

        <FadeUp className="md:col-span-6" delay={0.25}>
          <div className="h-full border border-white/10 bg-carbon p-8 hover:border-volt/40 hover:bg-steel transition-colors duration-300">
            <Users className="h-7 w-7 text-volt" />
            <h3 className="font-display font-bold uppercase text-lg sm:text-xl tracking-wide mt-6">
              Time-Bracket Leaderboards
            </h3>
            <p className="mt-3 text-sm text-neutral-500 leading-relaxed">
              Compete with members training the same minutes as you. The 10-minute bracket is as competitive as the 50.
            </p>
          </div>
        </FadeUp>
      </div>

      <FadeUp delay={0.1}>
        <p className="mt-20 font-mono text-xs tracking-[0.25em] uppercase text-volt">The Full Stack</p>
      </FadeUp>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-4">
        <FadeUp className="md:col-span-6 lg:col-span-4" delay={0.05}>
          <div data-testid="feature-exercise-library" className="h-full border border-white/10 bg-carbon p-8 hover:border-volt/40 hover:bg-steel transition-colors duration-300 flex flex-col">
            <Dumbbell className="h-7 w-7 text-volt" />
            <h3 className="font-display font-bold uppercase text-lg sm:text-xl tracking-wide mt-6">500+ Exercises. No Repeats.</h3>
            <p className="mt-3 text-sm text-neutral-500 leading-relaxed flex-1">
              Fit50&rsquo;s library covers bodyweight, dumbbell, resistance band, kettlebell, and circuit formats — over 500 movements across all muscle groups and all skill levels. Every exercise is demonstrated in crisp video by both a male and female coach, so you always train with form you can follow.
            </p>
            <span className="mt-6 inline-flex w-fit bg-volt px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase text-white">
              500+ Movements Across 12 Categories
            </span>
          </div>
        </FadeUp>

        <FadeUp className="md:col-span-6 lg:col-span-4" delay={0.1}>
          <div data-testid="feature-circuit-mode" className="h-full border border-white/10 bg-carbon p-8 hover:border-volt/40 hover:bg-steel transition-colors duration-300 flex flex-col bg-cover bg-center" style={{ backgroundImage: "linear-gradient(rgba(26, 26, 26, 0.88), rgba(26, 26, 26, 0.88)), url('https://static.prod-images.emergentagent.com/jobs/753a59ae-6295-4a2c-a309-76467ea4b06e/images/8a4d4a7a159705de5ecb79541fd0a0a57daccb3a06ee483a80790e90c8aec3e4.png')" }}>
            <Repeat className="h-7 w-7 text-volt" />
            <h3 className="font-display font-bold uppercase text-lg sm:text-xl tracking-wide mt-6">Circuit Mode. No Station Left Idle.</h3>
            <p className="mt-3 text-sm text-neutral-500 leading-relaxed flex-1">
              Circuit training in Fit50 chains compound movements with minimal rest, keeping your heart rate high and your clock honest. Choose a circuit-format session in any duration — from a 10-minute express circuit to a 50-minute full-station burn. Intensity is auto-calibrated so circuits at 10 minutes hit as hard as circuits at 50.
            </p>
            <span className="mt-6 inline-flex w-fit bg-volt px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase text-white">
              Available Across All 5 Durations
            </span>
          </div>
        </FadeUp>

        <FadeUp className="md:col-span-6 lg:col-span-4" delay={0.15}>
          <div data-testid="feature-meal-recipes" className="h-full border border-white/10 bg-carbon p-8 hover:border-volt/40 hover:bg-steel transition-colors duration-300 flex flex-col">
            <ChefHat className="h-7 w-7 text-volt" />
            <h3 className="font-display font-bold uppercase text-lg sm:text-xl tracking-wide mt-6">The Kitchen Matters As Much As The Workout.</h3>
            <p className="mt-3 text-sm text-neutral-500 leading-relaxed flex-1">
              Fit50 includes a full library of high-protein meal recipes designed to pair with your sessions. Post-HIIT recovery plates, pre-circuit fuel, and snacks sized for the time between your 10-minute morning session and your actual workday. Nutrition isn&rsquo;t a separate subscription — it&rsquo;s part of fit50.
            </p>
            <span className="mt-6 inline-flex w-fit bg-volt px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase text-white">
              Recipes Matched To Session Intensity
            </span>
          </div>
        </FadeUp>

        <FadeUp className="md:col-span-6" delay={0.2}>
          <div data-testid="feature-watch-coaching" className="h-full border border-white/10 bg-carbon p-8 hover:border-volt/40 hover:bg-steel transition-colors duration-300 flex flex-col">
            <Watch className="h-7 w-7 text-volt" />
            <h3 className="font-display font-bold uppercase text-lg sm:text-xl tracking-wide mt-6">Your Watch Is Already A Coach. Let It Coach.</h3>
            <p className="mt-3 text-sm text-neutral-500 leading-relaxed flex-1">
              Fit50 pairs with Apple Watch, Garmin, Wear OS, and all major heart rate monitors. During your session, live heart rate zones steer the work — if your rate drops below target, Fit50 pushes; if you&rsquo;re redlining too early, it pulls back. This isn&rsquo;t passive data logging. It&rsquo;s active, real-time coaching from hardware you already own.
            </p>
            <span className="mt-6 inline-flex w-fit bg-volt px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase text-white">
              Apple Watch | Garmin | Wear OS | Polar
            </span>
          </div>
        </FadeUp>

        <FadeUp className="md:col-span-6" delay={0.25}>
          <div data-testid="feature-dual-demos" className="h-full border border-white/10 bg-carbon p-8 hover:border-volt/40 hover:bg-steel transition-colors duration-300 flex flex-col">
            <Video className="h-7 w-7 text-volt" />
            <h3 className="font-display font-bold uppercase text-lg sm:text-xl tracking-wide mt-6">Form You Can Actually Follow.</h3>
            <p className="mt-3 text-sm text-neutral-500 leading-relaxed flex-1">
              Every exercise in fit50 is demonstrated by both a male and female coach. Different bodies move differently — showing both means nobody has to translate someone else&rsquo;s form. Pick the demonstration that matches how you move.
            </p>
            <span className="mt-6 inline-flex w-fit bg-volt px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase text-white">
              Dual-View Demonstrations On Every Movement
            </span>
          </div>
        </FadeUp>
      </div>

      <RecipeCarousel />
    </div>
  </section>
);
